/**
 * Central, typed registry of guided tours (EPIC #1321, part 1/9).
 *
 * Tours are declarative data — each step names a route, a stable
 * ``data-tour="..."`` anchor, optional permission/module gates, and the sidebar
 * accordion group to expand before highlighting. Copy carries the ``{agency}``
 * token, interpolated from BrandingContext at render time so the text stays
 * agency-neutral (D4-WLB / the D4-WLB-008 vendor-name guard).
 *
 * Parts 2/9..9/9 add the real per-surface tours here; this file ships only the
 * ``crm-orientation`` demo that exercises every engine capability (centered
 * step -> in-page anchor -> expand a collapsed nav group -> cross-route
 * navigation -> finish) plus permission/module gating on at least one step.
 */
import type { TourDefinition } from "./types";
import {
  WELCOME_TOURS,
  ACCOUNTS_WELCOME_TOUR,
  ADMIN_WELCOME_TOUR,
  AGENT_WELCOME_TOUR,
  CANVASSER_WELCOME_TOUR,
  CUSTOM_WELCOME_TOUR,
  FINANCE_WELCOME_TOUR,
  FINANCE_ACCOUNTS_WELCOME_TOUR,
  HR_WELCOME_TOUR,
  HR_PARALEGAL_WELCOME_TOUR,
  MANAGER_WELCOME_TOUR,
  OFFICE_ADMIN_WELCOME_TOUR,
  PARALEGAL_WELCOME_TOUR,
} from "./welcomeTours";
import { AGENT_SECTION_TOURS } from "./agentSectionTours";
import { ADMIN_SECTION_TOURS } from "./adminSectionTours";
import { ADMIN_PAGE_TOURS, OVERSIGHT_PAGE_TOURS } from "./adminPageTours";
import { AGENT_PAGE_TOURS } from "./agentPageTours";
import { TEST_DEAL_TOUR } from "./testDealTour";
import { PORTAL_PANEL_TOUR } from "./portalPanelTour";

/**
 * Demo / orientation tour. Proves the engine end-to-end and is the template the
 * later parts copy. Reachable for visual verification via ``?tour=crm-orientation``.
 *
 * Decision (issue #1522): KEPT as the documented engine-demo / visual-verification
 * fixture. It is intentionally excluded from the Training launcher and the
 * first-visit nudges (see isWelcomeOrDemoTour / WELCOME_AND_DEMO_TOUR_IDS) and is
 * reachable only via ?tour=crm-orientation. It exercises every engine capability
 * (centered step -> in-page anchor -> expand a collapsed nav group -> cross-route
 * navigation -> a gated step -> finish) end-to-end, which the persona tours do not
 * all do individually, so it stays as the regression/demo template. Do NOT remove.
 */
export const CRM_ORIENTATION_TOUR: TourDefinition = {
  id: "crm-orientation",
  title: "Welcome to {agency}",
  steps: [
    {
      id: "welcome",
      route: "/crm/dashboard",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "This quick tour shows you around your {agency} workspace. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "dashboard-overview",
      route: "/crm/dashboard",
      target: "dashboard-home",
      section: "Dashboard",
      title: "Your dashboard",
      body: "Your dashboard rolls up your own performance — pipeline, targets and earnings — updated in real time.",
      placement: "bottom",
    },
    {
      id: "nav-contacts",
      route: "/crm/dashboard",
      target: "nav-/crm/contacts",
      section: "Navigation",
      navGroup: "people",
      title: "Find your people",
      body: "Your contacts, farm and buyers live under Prospecting. We've opened the group so you can see it.",
      placement: "right",
    },
    {
      id: "contacts-page",
      route: "/crm/contacts",
      target: "page-contacts",
      section: "Contacts",
      title: "Your contacts",
      body: "This is your contact book. Search, filter and open any record to see its full history.",
      placement: "bottom",
    },
    {
      // Gated step: only users who can view commission (a standard agent does,
      // value-free back-office presets do not) see the earnings call-out. Proves
      // graceful per-step filtering (REQ D4-ONB-004) inside a live tour.
      id: "commission-note",
      route: "/crm/dashboard",
      target: "center",
      section: "Earnings",
      module: "financial",
      permFlag: "view_commission",
      title: "Track your earnings",
      body: "Your commission and targets are always a click away in My Stats.",
      placement: "center",
    },
    {
      id: "finish",
      route: "/crm/dashboard",
      target: "center",
      section: "Finish",
      title: "You're all set",
      body: "That's the tour. Explore at your own pace — your {agency} workspace is ready when you are.",
      placement: "center",
    },
  ],
};

/** Every registered tour, keyed by id. */
export const TOUR_REGISTRY: Record<string, TourDefinition> = {
  [CRM_ORIENTATION_TOUR.id]: CRM_ORIENTATION_TOUR,
  // The eleven persona welcome tours (3/9) -- offered once on first login via the
  // welcome prompt and replayable from Training.
  ...Object.fromEntries(WELCOME_TOURS.map((t) => [t.id, t])),
  // The agent CRM per-section mini-tours (4/9) -- opt-in deep dives, replayable
  // from Training and surfaced by the first-visit section nudge.
  ...Object.fromEntries(AGENT_SECTION_TOURS.map((t) => [t.id, t])),
  // The admin shell per-section mini-tours (5/9) -- one opt-in deep dive per
  // admin nav group, replayable from /admin/training and surfaced by the nudge.
  ...Object.fromEntries(ADMIN_SECTION_TOURS.map((t) => [t.id, t])),
  // The per-PAGE admin-shell tours (EPIC #1952 Wave 2, #1955) -- one focused
  // tour per COMPLEX admin page, wired to its route in routeTours.ts so the
  // header GraduationCap launcher starts it on that page.
  ...Object.fromEntries(ADMIN_PAGE_TOURS.map((t) => [t.id, t])),
  // The manager read-only OVERSIGHT page-tour variants (#2452) -- each mirrors an
  // admin page tour onto its /crm/oversight/* keep-in-shell mount, audience-gated
  // to view_management_dashboards, route-wired in routeTours.ts.
  ...Object.fromEntries(OVERSIGHT_PAGE_TOURS.map((t) => [t.id, t])),
  // The per-PAGE agent-CRM + back-office desk tours (EPIC #1952 Wave 3, #1956)
  // -- one focused tour per COMPLEX agent page (Farm / Buyers / Agenda /
  // Messaging / FICA matrix / Referrals / Conveyancers / Connect / deal detail)
  // plus the File Cover Page and Behind Schedule desk pages, route-wired in
  // routeTours.ts so the header launcher starts each on its page.
  ...Object.fromEntries(AGENT_PAGE_TOURS.map((t) => [t.id, t])),
  // The standalone "Practice safely with a test deal" tour (#2195) -- a
  // LAUNCHER-ONLY guided walkthrough of create -> archive/restore -> admin
  // permanent-delete. Training-launchable + ?tour=test-deal deep-linkable; it is
  // NOT route-wired to /crm/deals (that stays agent-deals) and is excluded from
  // the first-visit nudge (sectionNudges.ts), so it never competes for the
  // /crm/deals launcher slot.
  [TEST_DEAL_TOUR.id]: TEST_DEAL_TOUR,
  // The staff "Client portal" tour (D9 WP24, #3551) -- LAUNCHER-ONLY
  // (Training + ?tour=portal-deal-panel). It opens on /crm/deals, hands off
  // when the user opens a deal (advanceOnRoute) and then walks the deal's
  // Client portal + Partner sharing panels. Not route-wired, never nudged.
  [PORTAL_PANEL_TOUR.id]: PORTAL_PANEL_TOUR,
  // NB the back-office per-section desk mini-tours were REMOVED (#1521): for a
  // single-surface back-office persona the welcome tour and the desk section
  // tour covered ~90% the same ground on the same landing, double-nudging the
  // user. The persona welcome tour (welcomeTours.ts) is the single canonical
  // journey -- relaunchable as the launcher's primary CTA -- and now folds in
  // that coverage (e.g. the manager Oversight suite, the office-admin listing).
};

/** Look up a tour definition by id (undefined when unknown). */
export function getTour(tourId: string): TourDefinition | undefined {
  return TOUR_REGISTRY[tourId];
}

/**
 * The ids of the eleven persona welcome tours plus the internal orientation demo
 * (REQ D4-ONB-009). The launcher uses this to keep the welcome variants -- which
 * all share the same "Welcome to {agency}" title -- and the demo tour OUT of the
 * section-tour sublist, surfacing only the CURRENT user's welcome tour as the
 * single primary CTA. Read-only; does not define or modify any tour.
 */
const WELCOME_AND_DEMO_TOUR_IDS: ReadonlySet<string> = new Set<string>([
  CRM_ORIENTATION_TOUR.id,
  ...WELCOME_TOURS.map((t) => t.id),
]);

/**
 * True when ``tourId`` is a persona welcome tour or the internal orientation
 * demo -- i.e. NOT one of the per-section deep-dive mini-tours. The Guided Tours
 * launcher lists the section tours and excludes these (the user's own welcome
 * tour is shown separately as the primary CTA). Read-only.
 */
export function isWelcomeOrDemoTour(tourId: string): boolean {
  return WELCOME_AND_DEMO_TOUR_IDS.has(tourId);
}

/**
 * The welcome-tour id for a user, by role + permission preset (D4-ONB-007).
 *
 * Mirrors ``landingPathForUser`` (the single landing-path source of truth): each
 * persona lands on a different surface and gets the matching welcome journey. An
 * office-admin or unknown/CUSTOM preset that lands on the agent dashboard gets
 * the office-admin / custom spine respectively; a plain field agent gets the
 * agent spine. The returned tour is still step-gated at runtime so it degrades
 * gracefully (D4-ONB-004).
 */
export function welcomeTourIdForUser(
  role: string | undefined,
  preset: string | null | undefined,
): string {
  if (role === "admin") return ADMIN_WELCOME_TOUR.id;
  switch (preset) {
    // #1520: the combined Finance/Accounts persona has its own dedicated welcome
    // journey that stays on /finance-accounts and covers BOTH the
    // commission-release (Finance) and deposit-to-invest (Accounts) halves --
    // it no longer reuses the Finance-only tour (which bounced the user to the
    // /finance half-surface).
    case "finance_accounts":
      return FINANCE_ACCOUNTS_WELCOME_TOUR.id;
    case "finance":
      return FINANCE_WELCOME_TOUR.id;
    case "paralegal":
      return PARALEGAL_WELCOME_TOUR.id;
    case "manager":
      return MANAGER_WELCOME_TOUR.id;
    case "accounts":
      return ACCOUNTS_WELCOME_TOUR.id;
    case "office_admin":
      return OFFICE_ADMIN_WELCOME_TOUR.id;
    case "hr":
      return HR_WELCOME_TOUR.id;
    // D5-RBP-005 (#4175): the combined HR/Paralegal desk lands on /paralegal
    // and gets its own journey covering both desks.
    case "hr_paralegal":
      return HR_PARALEGAL_WELCOME_TOUR.id;
    // #2467: the canvasser works the farmbook -- its welcome tour lands on
    // /crm/farm. Audience-gated to the canvasser preset, so a plain agent on
    // /crm/farm is never offered it.
    case "canvasser":
      return CANVASSER_WELCOME_TOUR.id;
    case "custom":
      return CUSTOM_WELCOME_TOUR.id;
    default:
      return AGENT_WELCOME_TOUR.id;
  }
}
